import jsPDF from 'jspdf';
import type { Project, ExportOptions, Chapter, SectionType } from '../types';

interface PageDimensions {
  width: number;
  height: number;
  marginTop: number;
  marginBottom: number;
  marginInner: number;
  marginOuter: number;
}

// Page sizes in mm - optimized for KDP print requirements
const PAGE_SIZES: Record<string, PageDimensions> = {
  'a4': { width: 210, height: 297, marginTop: 25, marginBottom: 25, marginInner: 25, marginOuter: 20 },
  'a5': { width: 148, height: 210, marginTop: 20, marginBottom: 20, marginInner: 20, marginOuter: 15 },
  'letter': { width: 216, height: 279, marginTop: 25, marginBottom: 25, marginInner: 25, marginOuter: 20 },
  '6x9': { width: 152.4, height: 228.6, marginTop: 19, marginBottom: 19, marginInner: 22, marginOuter: 16 },
  '5x8': { width: 127, height: 203.2, marginTop: 16, marginBottom: 16, marginInner: 19, marginOuter: 13 },
  '5.5x8.5': { width: 139.7, height: 215.9, marginTop: 19, marginBottom: 19, marginInner: 19, marginOuter: 13 },
};

const MARGIN_MULTIPLIERS: Record<string, number> = {
  narrow: 0.7,
  normal: 1,
  wide: 1.3,
};

const FONT_SIZES: Record<string, number> = {
  small: 10,
  medium: 11,
  large: 12,
};

const LINE_HEIGHTS: Record<string, number> = {
  single: 1.2,
  '1.5': 1.5,
  double: 2.0,
};

// jsPDF runs in mm but setFontSize() is in points. Vertical advances must be
// converted pt → mm, otherwise text either overlaps or leaves huge gaps.
const PT_TO_MM = 0.352778;

/** Line advance in mm for a given font size (pt) and line-height multiplier. */
function lineMm(sizePt: number, lh: number): number {
  return sizePt * lh * PT_TO_MM;
}

/**
 * Loads an image (data URL or remote) and returns a JPEG data URL plus its
 * intrinsic size. Returns null on failure (e.g. CORS) so export never breaks.
 */
async function loadImageData(
  url: string
): Promise<{ dataUrl: string; width: number; height: number } | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image load failed'));
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const cctx = canvas.getContext('2d');
    if (!cctx) return null;
    // White matte so any transparency doesn't render black on the page.
    cctx.fillStyle = '#ffffff';
    cctx.fillRect(0, 0, canvas.width, canvas.height);
    cctx.drawImage(img, 0, 0);
    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.92),
      width: img.naturalWidth,
      height: img.naturalHeight,
    };
  } catch {
    return null;
  }
}

// Section categories for pagination rules
const FRONT_MATTER_SECTIONS: SectionType[] = [
  'half-title', 'title-page', 'copyright', 'dedication', 'epigraph',
  'toc', 'foreword', 'preface', 'introduction'
];

const BODY_SECTIONS: SectionType[] = [
  'part', 'prologue', 'chapter', 'interlude', 'epilogue'
];

const BACK_MATTER_SECTIONS: SectionType[] = [
  'afterword', 'acknowledgments', 'appendix', 'glossary',
  'bibliography', 'notes', 'about-author', 'also-by'
];

// Narrative sections that get a decorative drop cap on their first paragraph.
const DROP_CAP_SECTIONS: SectionType[] = ['prologue', 'chapter', 'interlude', 'epilogue'];

// Page start rules for each section type
// 'recto' = right page (odd), 'verso' = left page (even), 'any' = doesn't matter
type PageStartRule = 'recto' | 'verso' | 'any';

const SECTION_PAGE_RULES: Record<SectionType, PageStartRule> = {
  'half-title': 'recto',
  'title-page': 'recto',
  'copyright': 'verso',
  'dedication': 'recto',
  'epigraph': 'recto',
  'toc': 'recto',
  'foreword': 'recto',
  'preface': 'recto',
  'introduction': 'recto',
  'part': 'recto',
  'prologue': 'recto',
  'chapter': 'recto',
  'interlude': 'any',
  'epilogue': 'recto',
  'afterword': 'recto',
  'acknowledgments': 'recto',
  'appendix': 'recto',
  'glossary': 'recto',
  'bibliography': 'recto',
  'notes': 'recto',
  'about-author': 'recto',
  'also-by': 'recto',
};

// Whether section should show page numbers
const SECTION_SHOW_PAGE_NUMBERS: Record<SectionType, boolean> = {
  'half-title': false,
  'title-page': false,
  'copyright': false,
  'dedication': false,
  'epigraph': false,
  'toc': true,
  'foreword': true,
  'preface': true,
  'introduction': true,
  'part': false,
  'prologue': true,
  'chapter': true,
  'interlude': true,
  'epilogue': true,
  'afterword': true,
  'acknowledgments': true,
  'appendix': true,
  'glossary': true,
  'bibliography': true,
  'notes': true,
  'about-author': true,
  'also-by': true,
};

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function parseHtmlContent(html: string): Array<{ type: 'paragraph' | 'heading' | 'break' | 'blockquote'; text: string; level?: number }> {
  const div = document.createElement('div');
  div.innerHTML = html;

  const elements: Array<{ type: 'paragraph' | 'heading' | 'break' | 'blockquote'; text: string; level?: number }> = [];

  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        elements.push({ type: 'paragraph', text });
      }
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tagName = el.tagName.toLowerCase();

      if (tagName === 'br') {
        elements.push({ type: 'break', text: '' });
        return;
      }

      if (tagName.match(/^h[1-6]$/)) {
        const level = parseInt(tagName[1]);
        elements.push({ type: 'heading', text: el.textContent?.trim() || '', level });
        return;
      }

      if (tagName === 'blockquote') {
        elements.push({ type: 'blockquote', text: el.textContent?.trim() || '' });
        return;
      }

      if (tagName === 'p') {
        const text = el.textContent?.trim();
        if (text) {
          elements.push({ type: 'paragraph', text });
        }
        return;
      }

      el.childNodes.forEach(processNode);
    }
  };

  div.childNodes.forEach(processNode);

  if (elements.length === 0) {
    const plainText = stripHtml(html);
    plainText.split(/\n\n+/).forEach(para => {
      const text = para.trim();
      if (text) {
        elements.push({ type: 'paragraph', text });
      }
    });
  }

  return elements;
}

function wrapText(pdf: jsPDF, text: string, maxWidth: number): string[] {
  return pdf.splitTextToSize(text, maxWidth);
}

function toRomanNumeral(num: number): string {
  const romanNumerals = [
    { value: 1000, numeral: 'm' },
    { value: 900, numeral: 'cm' },
    { value: 500, numeral: 'd' },
    { value: 400, numeral: 'cd' },
    { value: 100, numeral: 'c' },
    { value: 90, numeral: 'xc' },
    { value: 50, numeral: 'l' },
    { value: 40, numeral: 'xl' },
    { value: 10, numeral: 'x' },
    { value: 9, numeral: 'ix' },
    { value: 5, numeral: 'v' },
    { value: 4, numeral: 'iv' },
    { value: 1, numeral: 'i' },
  ];

  let result = '';
  let remaining = num;

  for (const { value, numeral } of romanNumerals) {
    while (remaining >= value) {
      result += numeral;
      remaining -= value;
    }
  }

  return result;
}

function getSectionDisplayName(sectionType: SectionType): string {
  const names: Record<SectionType, string> = {
    'half-title': '',
    'title-page': '',
    'copyright': 'Copyright',
    'dedication': 'Dedication',
    'epigraph': '',
    'toc': 'Contents',
    'foreword': 'Foreword',
    'preface': 'Preface',
    'introduction': 'Introduction',
    'part': 'Part',
    'prologue': 'Prologue',
    'chapter': 'Chapter',
    'interlude': '',
    'epilogue': 'Epilogue',
    'afterword': 'Afterword',
    'acknowledgments': 'Acknowledgments',
    'appendix': 'Appendix',
    'glossary': 'Glossary',
    'bibliography': 'Bibliography',
    'notes': 'Notes',
    'about-author': 'About the Author',
    'also-by': 'Also By',
  };
  return names[sectionType] || '';
}

// Per physical page (1-based) metadata used to place folios + running heads
// according to book typography rules.
interface PageMeta {
  isRight: boolean; // recto/verso as laid out (mirrors ctx.isRightPage)
  isBlank: boolean; // spacer page — counts in the sequence, never printed
  isCover: boolean; // full-bleed cover — no folio/head
  isOpening: boolean; // first page of a section (chapter openings drop the folio)
  sectionType?: SectionType;
  chapterTitle?: string; // controls the recto running head
}

// A table-of-contents row whose page number is filled in after the whole book
// has been paginated (the body doesn't exist yet when the TOC is rendered).
interface TocEntry {
  page: number; // physical page the TOC row sits on
  y: number; // baseline of the row
  titleEndX: number; // x where the title ends (dot leaders start here)
  chapterId: string;
}

interface PdfContext {
  pdf: jsPDF;
  dimensions: PageDimensions;
  baseFontSize: number;
  lineHeight: number;
  fontName: string;
  contentWidth: number;
  currentY: number;
  currentPage: number;
  isRightPage: boolean;
  frontMatterPageCount: number;
  bodyPageCount: number;
  coverPages: number;
  bodyStartPage: number;
  inFrontMatter: boolean;
  includePageNumbers: boolean;
  dropCaps: boolean;
  headerFooter: string;
  chapterStartPage: string;
  pageMeta: PageMeta[];
  tocEntries: TocEntry[];
  project: Project;
}

export async function exportToPDF(
  project: Project,
  options: ExportOptions
): Promise<Blob> {
  const {
    pageSize,
    fontSize,
    fontFamily,
    lineSpacing,
    margins,
    includeTitle,
    includeToc,
    includeCopyright,
    chapterStartPage,
    includePageNumbers,
    headerFooter,
  } = options;

  const baseDimensions = PAGE_SIZES[pageSize];
  const marginMultiplier = MARGIN_MULTIPLIERS[margins];

  const dimensions: PageDimensions = {
    ...baseDimensions,
    marginTop: baseDimensions.marginTop * marginMultiplier,
    marginBottom: baseDimensions.marginBottom * marginMultiplier,
    marginInner: baseDimensions.marginInner * marginMultiplier,
    marginOuter: baseDimensions.marginOuter * marginMultiplier,
  };

  const baseFontSize = FONT_SIZES[fontSize];
  const lineHeight = LINE_HEIGHTS[lineSpacing];
  const fontName = fontFamily === 'serif' ? 'times' : 'helvetica';

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [dimensions.width, dimensions.height],
  });

  const contentWidth = dimensions.width - dimensions.marginInner - dimensions.marginOuter;

  const ctx: PdfContext = {
    pdf,
    dimensions,
    baseFontSize,
    lineHeight,
    fontName,
    contentWidth,
    currentY: dimensions.marginTop,
    currentPage: 1,
    isRightPage: true,
    frontMatterPageCount: 0,
    bodyPageCount: 0,
    coverPages: 0,
    bodyStartPage: 1,
    inFrontMatter: true,
    includePageNumbers,
    dropCaps: options.dropCaps ?? true,
    headerFooter,
    chapterStartPage,
    pageMeta: [],
    tocEntries: [],
    project,
  };

  // Page 1 starts as a recto.
  ctx.pageMeta[1] = { isRight: true, isBlank: false, isCover: false, isOpening: false };

  // Cover page (full-bleed) — page 1. Skipped for print-ready files (KDP wants
  // the cover uploaded separately).
  if (options.includeCover && !options.printReady && project.metadata.coverImage) {
    const cover = await loadImageData(project.metadata.coverImage);
    if (cover) {
      pdf.addImage(cover.dataUrl, 'JPEG', 0, 0, dimensions.width, dimensions.height, undefined, 'FAST');
      ctx.coverPages = 1;
      ctx.pageMeta[1].isCover = true;
      // Move to a fresh recto for the book block (the cover sits outside the
      // recto/verso flow, like a real printed cover).
      pdf.addPage();
      ctx.currentPage = 2;
      ctx.isRightPage = true;
      ctx.currentY = dimensions.marginTop;
      ctx.pageMeta[2] = { isRight: true, isBlank: false, isCover: false, isOpening: false };
    }
  }

  // Categorize chapters
  const frontMatterChapters = project.chapters.filter(ch =>
    FRONT_MATTER_SECTIONS.includes(ch.sectionType)
  );
  const bodyChapters = project.chapters.filter(ch =>
    BODY_SECTIONS.includes(ch.sectionType)
  );
  const backMatterChapters = project.chapters.filter(ch =>
    BACK_MATTER_SECTIONS.includes(ch.sectionType)
  );

  // Track page assignments for TOC
  const pageAssignments: Map<string, { pageNum: number; isRoman: boolean }> = new Map();

  // Process front matter
  for (const chapter of frontMatterChapters) {
    if (chapter.sectionType === 'title-page' && !includeTitle) continue;
    if (chapter.sectionType === 'copyright' && !includeCopyright) continue;
    if (chapter.sectionType === 'toc' && !includeToc) continue;

    ensureSectionStart(ctx, chapter.sectionType);
    pageAssignments.set(chapter.id, {
      pageNum: ctx.frontMatterPageCount + 1,
      isRoman: true
    });
    renderChapter(ctx, chapter, project);
  }

  // Transition to body - ensure body starts on recto
  ctx.inFrontMatter = false;
  if (!ctx.isRightPage) {
    addPage(ctx);
  }
  ctx.bodyStartPage = ctx.currentPage;

  // Process body chapters — each MUST start on a fresh page (this was the bug:
  // chapters were drawn over the previous one when recto/verso already matched).
  for (const chapter of bodyChapters) {
    const rule: PageStartRule =
      chapterStartPage === 'right' ? 'recto'
      : chapterStartPage === 'left' ? 'verso'
      : chapterStartPage === 'any' ? 'any'
      : SECTION_PAGE_RULES[chapter.sectionType];
    startSectionPage(ctx, rule);

    // Printed arabic folio (page 1 = first body page; continuous into back matter).
    pageAssignments.set(chapter.id, {
      pageNum: ctx.currentPage - ctx.bodyStartPage + 1,
      isRoman: false
    });
    renderChapter(ctx, chapter, project);
  }

  // Process back matter — continues the arabic sequence (does not restart)
  for (const chapter of backMatterChapters) {
    ensureSectionStart(ctx, chapter.sectionType);
    pageAssignments.set(chapter.id, {
      pageNum: ctx.currentPage - ctx.bodyStartPage + 1,
      isRoman: false
    });
    renderChapter(ctx, chapter, project);
  }

  // Folios + running heads, then backfill the TOC page numbers.
  addPageFurniture(ctx);
  fillTocPageNumbers(ctx, pageAssignments);

  return pdf.output('blob');
}

function getLeftMargin(ctx: PdfContext): number {
  return ctx.isRightPage ? ctx.dimensions.marginInner : ctx.dimensions.marginOuter;
}

function addPage(ctx: PdfContext): void {
  ctx.pdf.addPage();
  ctx.currentPage++;
  ctx.isRightPage = !ctx.isRightPage;
  ctx.currentY = ctx.dimensions.marginTop;
  ctx.pageMeta[ctx.currentPage] = {
    isRight: ctx.isRightPage,
    isBlank: false,
    isCover: false,
    isOpening: false,
  };

  if (ctx.inFrontMatter) {
    ctx.frontMatterPageCount++;
  } else {
    ctx.bodyPageCount++;
  }
}

function addBlankPage(ctx: PdfContext): void {
  addPage(ctx);
  // Blank spacer page — counts in the sequence but never prints a folio/head.
  ctx.pageMeta[ctx.currentPage].isBlank = true;
}

function ensurePagePosition(ctx: PdfContext, rule: PageStartRule): void {
  if (rule === 'any') return;

  if (rule === 'recto' && !ctx.isRightPage) {
    addBlankPage(ctx);
  } else if (rule === 'verso' && ctx.isRightPage) {
    addBlankPage(ctx);
  }
}

/**
 * Starts a section on a guaranteed-fresh page, then applies the recto/verso
 * rule. A page only counts as "fresh" when nothing has been drawn on it yet
 * (currentY still at the top margin).
 */
function startSectionPage(ctx: PdfContext, rule: PageStartRule): void {
  if (ctx.currentY > ctx.dimensions.marginTop + 1) {
    addPage(ctx);
  }
  ensurePagePosition(ctx, rule);
}

function ensureSectionStart(ctx: PdfContext, sectionType: SectionType): void {
  const rule = SECTION_PAGE_RULES[sectionType];

  // Always start a new page for new sections (except copyright which follows title page)
  if (sectionType !== 'copyright' && ctx.currentPage > 1) {
    if (ctx.currentY > ctx.dimensions.marginTop + 10) {
      addPage(ctx);
    }
  }

  ensurePagePosition(ctx, rule);
}

function checkPageBreak(ctx: PdfContext, neededSpace: number): boolean {
  if (ctx.currentY + neededSpace > ctx.dimensions.height - ctx.dimensions.marginBottom) {
    addPage(ctx);
    return true;
  }
  return false;
}

function renderChapter(ctx: PdfContext, chapter: Chapter, project: Project): void {
  const { pdf, dimensions, baseFontSize, lineHeight, fontName, contentWidth } = ctx;

  // Mark the section's opening page (folios are dropped here; running heads too).
  const meta = ctx.pageMeta[ctx.currentPage];
  if (meta) {
    meta.isOpening = true;
    meta.sectionType = chapter.sectionType;
    meta.chapterTitle = chapter.title;
  }

  // Special rendering for different section types
  switch (chapter.sectionType) {
    case 'half-title':
      renderHalfTitle(ctx, project);
      return;

    case 'title-page':
      renderTitlePage(ctx, project);
      return;

    case 'copyright':
      renderCopyrightPage(ctx, project);
      return;

    case 'dedication':
      renderDedication(ctx, chapter);
      return;

    case 'epigraph':
      renderEpigraph(ctx, chapter);
      return;

    case 'toc':
      renderTableOfContents(ctx, project);
      return;

    case 'part':
      renderPartPage(ctx, chapter);
      return;

    default:
      renderStandardChapter(ctx, chapter, project);
  }
}

function renderHalfTitle(ctx: PdfContext, project: Project): void {
  const { pdf, dimensions, baseFontSize, fontName } = ctx;

  // Half title appears at about 1/3 down the page, centered
  ctx.currentY = dimensions.height / 3;

  pdf.setFontSize(baseFontSize * 2);
  pdf.setFont(fontName, 'bold');

  const title = project.metadata.title || 'Untitled';
  const titleWidth = pdf.getTextWidth(title);
  pdf.text(title, (dimensions.width - titleWidth) / 2, ctx.currentY);
}

function renderTitlePage(ctx: PdfContext, project: Project): void {
  const { pdf, dimensions, baseFontSize, fontName, contentWidth } = ctx;

  // Title at about 1/3 down
  ctx.currentY = dimensions.height / 3;

  pdf.setFontSize(baseFontSize * 2.5);
  pdf.setFont(fontName, 'bold');

  const title = project.metadata.title || 'Untitled';
  const titleLines = wrapText(pdf, title, contentWidth);

  titleLines.forEach((line, index) => {
    const lineWidth = pdf.getTextWidth(line);
    pdf.text(line, (dimensions.width - lineWidth) / 2, ctx.currentY + index * baseFontSize * 1.2);
  });

  ctx.currentY += titleLines.length * baseFontSize * 1.2 + 15;

  // Subtitle
  if (project.metadata.subtitle) {
    pdf.setFontSize(baseFontSize * 1.3);
    pdf.setFont(fontName, 'italic');
    const subtitleWidth = pdf.getTextWidth(project.metadata.subtitle);
    pdf.text(project.metadata.subtitle, (dimensions.width - subtitleWidth) / 2, ctx.currentY);
    ctx.currentY += baseFontSize * 2;
  }

  // "A Novel" or genre
  if (project.metadata.genre) {
    pdf.setFontSize(baseFontSize);
    pdf.setFont(fontName, 'italic');
    const genreWidth = pdf.getTextWidth(project.metadata.genre);
    pdf.text(project.metadata.genre, (dimensions.width - genreWidth) / 2, ctx.currentY);
    ctx.currentY += baseFontSize * 3;
  }

  // Author at about 60% down
  ctx.currentY = dimensions.height * 0.6;

  if (project.metadata.author) {
    pdf.setFontSize(baseFontSize * 1.5);
    pdf.setFont(fontName, 'normal');
    const authorWidth = pdf.getTextWidth(project.metadata.author);
    pdf.text(project.metadata.author, (dimensions.width - authorWidth) / 2, ctx.currentY);
  }

  // Publisher at bottom
  if (project.metadata.publisher) {
    ctx.currentY = dimensions.height - dimensions.marginBottom - baseFontSize * 2;
    pdf.setFontSize(baseFontSize * 0.9);
    pdf.setFont(fontName, 'normal');
    const pubWidth = pdf.getTextWidth(project.metadata.publisher);
    pdf.text(project.metadata.publisher, (dimensions.width - pubWidth) / 2, ctx.currentY);
  }
}

function renderCopyrightPage(ctx: PdfContext, project: Project): void {
  const { pdf, dimensions, baseFontSize, fontName, contentWidth } = ctx;

  // Copyright info starts at about 70% down the page (verso page convention)
  ctx.currentY = dimensions.height * 0.65;

  pdf.setFontSize(baseFontSize * 0.85);
  pdf.setFont(fontName, 'normal');

  const centerText = (text: string) => {
    const width = pdf.getTextWidth(text);
    pdf.text(text, (dimensions.width - width) / 2, ctx.currentY);
    ctx.currentY += baseFontSize * 0.6;
  };

  const leftText = (text: string) => {
    pdf.text(text, getLeftMargin(ctx), ctx.currentY);
    ctx.currentY += baseFontSize * 0.6;
  };

  // Copyright notice
  const year = new Date().getFullYear();
  const author = project.metadata.author || 'Author';
  centerText(`Copyright © ${year} ${author}`);
  ctx.currentY += baseFontSize * 0.3;

  // Rights statement
  centerText('All rights reserved.');
  ctx.currentY += baseFontSize * 0.5;

  // Additional copyright text
  const copyrightText = 'No part of this publication may be reproduced, distributed, or transmitted in any form or by any means, including photocopying, recording, or other electronic or mechanical methods, without the prior written permission of the publisher, except in the case of brief quotations embodied in critical reviews and certain other noncommercial uses permitted by copyright law.';

  pdf.setFontSize(baseFontSize * 0.75);
  const lines = wrapText(pdf, copyrightText, contentWidth * 0.85);
  lines.forEach(line => {
    const width = pdf.getTextWidth(line);
    pdf.text(line, (dimensions.width - width) / 2, ctx.currentY);
    ctx.currentY += baseFontSize * 0.5;
  });

  ctx.currentY += baseFontSize;

  // ISBN
  if (project.metadata.isbn) {
    pdf.setFontSize(baseFontSize * 0.85);
    centerText(`ISBN: ${project.metadata.isbn}`);
    ctx.currentY += baseFontSize * 0.3;
  }

  // Publisher
  if (project.metadata.publisher) {
    centerText(project.metadata.publisher);
  }

  // Edition
  ctx.currentY += baseFontSize * 0.5;
  centerText('First Edition');
}

function renderDedication(ctx: PdfContext, chapter: Chapter): void {
  const { pdf, dimensions, baseFontSize, fontName, contentWidth } = ctx;

  // Dedication is centered vertically
  ctx.currentY = dimensions.height * 0.4;

  pdf.setFontSize(baseFontSize * 1.1);
  pdf.setFont(fontName, 'italic');

  const content = stripHtml(chapter.content);
  const lines = wrapText(pdf, content, contentWidth * 0.7);

  lines.forEach(line => {
    const width = pdf.getTextWidth(line);
    pdf.text(line, (dimensions.width - width) / 2, ctx.currentY);
    ctx.currentY += baseFontSize * 1.3;
  });
}

function renderEpigraph(ctx: PdfContext, chapter: Chapter): void {
  const { pdf, dimensions, baseFontSize, fontName, contentWidth } = ctx;

  // Epigraph is positioned at about 40% down
  ctx.currentY = dimensions.height * 0.4;

  const content = stripHtml(chapter.content);

  // Split by potential attribution (look for — or - at the start of last line)
  const parts = content.split(/\n/);
  let quote = content;
  let attribution = '';

  if (parts.length > 1) {
    const lastLine = parts[parts.length - 1].trim();
    if (lastLine.startsWith('—') || lastLine.startsWith('-')) {
      attribution = lastLine.replace(/^[—-]\s*/, '');
      quote = parts.slice(0, -1).join('\n');
    }
  }

  // Quote
  pdf.setFontSize(baseFontSize * 1.05);
  pdf.setFont(fontName, 'italic');

  const quoteLines = wrapText(pdf, quote, contentWidth * 0.65);
  const quoteStartX = dimensions.width * 0.25;

  quoteLines.forEach(line => {
    pdf.text(line, quoteStartX, ctx.currentY);
    ctx.currentY += baseFontSize * 1.4;
  });

  // Attribution
  if (attribution) {
    ctx.currentY += baseFontSize * 0.5;
    pdf.setFontSize(baseFontSize * 0.9);
    pdf.setFont(fontName, 'normal');
    const attrText = `— ${attribution}`;
    pdf.text(attrText, quoteStartX + 20, ctx.currentY);
  }
}

function renderTableOfContents(ctx: PdfContext, project: Project): void {
  const { pdf, dimensions, baseFontSize, lineHeight, fontName, contentWidth } = ctx;

  ctx.currentY = dimensions.marginTop + 30;

  // TOC Title
  pdf.setFontSize(baseFontSize * 1.8);
  pdf.setFont(fontName, 'bold');

  const tocTitle = 'Contents';
  const titleWidth = pdf.getTextWidth(tocTitle);
  pdf.text(tocTitle, (dimensions.width - titleWidth) / 2, ctx.currentY);

  ctx.currentY += lineMm(baseFontSize, lineHeight) * 2;

  // TOC entries
  pdf.setFontSize(baseFontSize);
  pdf.setFont(fontName, 'normal');

  // Only include main content in TOC
  const tocSections: SectionType[] = ['prologue', 'part', 'chapter', 'epilogue', 'afterword', 'acknowledgments', 'about-author'];

  const tocChapters = project.chapters.filter(ch => tocSections.includes(ch.sectionType));
  const entryLine = lineMm(baseFontSize, Math.max(lineHeight, 1.5));

  let chapterNumber = 0;

  tocChapters.forEach((chapter) => {
    checkPageBreak(ctx, entryLine);

    let displayTitle = chapter.title;
    const isPart = chapter.sectionType === 'part';

    if (chapter.sectionType === 'chapter') {
      chapterNumber++;
      if (!/^(cap[íi]tulo|chapter)\b/i.test(chapter.title.trim())) {
        displayTitle = `${chapterNumber}. ${chapter.title}`;
      }
    }
    if (isPart) {
      pdf.setFont(fontName, 'bold');
      ctx.currentY += entryLine * 0.4;
    }

    // Draw entry title; remember where it ends so dot leaders + the page
    // number can be backfilled once the book is fully paginated.
    const leftMargin = getLeftMargin(ctx);
    pdf.text(displayTitle, leftMargin, ctx.currentY);
    ctx.tocEntries.push({
      page: ctx.currentPage,
      y: ctx.currentY,
      titleEndX: leftMargin + pdf.getTextWidth(displayTitle),
      chapterId: chapter.id,
    });

    if (isPart) {
      pdf.setFont(fontName, 'normal');
      ctx.currentY += entryLine * 0.4;
    }

    ctx.currentY += entryLine;
  });
}

function renderPartPage(ctx: PdfContext, chapter: Chapter): void {
  const { pdf, dimensions, baseFontSize, fontName } = ctx;

  // Part divider pages are centered vertically
  ctx.currentY = dimensions.height * 0.4;

  // Part label (e.g., "PART ONE")
  pdf.setFontSize(baseFontSize * 1.2);
  pdf.setFont(fontName, 'normal');

  const partLabel = 'PART';
  const labelWidth = pdf.getTextWidth(partLabel);
  pdf.text(partLabel, (dimensions.width - labelWidth) / 2, ctx.currentY);

  ctx.currentY += baseFontSize * 2;

  // Part title
  pdf.setFontSize(baseFontSize * 2);
  pdf.setFont(fontName, 'bold');

  const title = chapter.title || 'I';
  const titleWidth = pdf.getTextWidth(title);
  pdf.text(title, (dimensions.width - titleWidth) / 2, ctx.currentY);
}

/**
 * Renders a paragraph with a 3-line decorative drop cap. The first letter is
 * drawn large at the left margin; the opening lines flow to its right, then the
 * text returns to the full content width.
 */
function renderDropCapParagraph(ctx: PdfContext, text: string, bodyLine: number): void {
  const { pdf, baseFontSize, fontName, contentWidth } = ctx;
  const dropLines = 3;
  const firstChar = text.charAt(0);
  const rest = text.slice(1);
  const leftMargin = getLeftMargin(ctx);

  // Keep the whole cap on one page.
  checkPageBreak(ctx, bodyLine * dropLines);
  const startY = ctx.currentY;

  // Drop cap glyph — roughly `dropLines` lines tall, sitting on the last line's baseline.
  const capSizePt = baseFontSize * 6;
  pdf.setFontSize(capSizePt);
  pdf.setFont(fontName, 'bold');
  const capWidth = pdf.getTextWidth(firstChar);
  pdf.text(firstChar, leftMargin, startY + (dropLines - 1) * bodyLine);

  // Opening lines flow to the right of the cap.
  pdf.setFontSize(baseFontSize);
  pdf.setFont(fontName, 'normal');
  const gap = 2.5;
  const indentX = leftMargin + capWidth + gap;
  const indentedWidth = contentWidth - capWidth - gap;

  const narrowLines: string[] = pdf.splitTextToSize(rest, indentedWidth);
  const besideCount = Math.min(dropLines, narrowLines.length);

  for (let i = 0; i < besideCount; i++) {
    pdf.text(narrowLines[i], indentX, ctx.currentY);
    ctx.currentY += bodyLine;
  }

  // Don't let following text climb above the bottom of the cap.
  if (ctx.currentY < startY + dropLines * bodyLine) {
    ctx.currentY = startY + dropLines * bodyLine;
  }

  // Reconstruct the remainder by words (robust against spacing differences) and
  // render it at the full content width.
  const consumedWords = narrowLines.slice(0, besideCount).join(' ').split(/\s+/).filter(Boolean).length;
  const remainder = rest.split(/\s+/).filter(Boolean).slice(consumedWords).join(' ');
  if (remainder) {
    const fullLines: string[] = pdf.splitTextToSize(remainder, contentWidth);
    fullLines.forEach((line) => {
      checkPageBreak(ctx, bodyLine);
      pdf.text(line, leftMargin, ctx.currentY);
      ctx.currentY += bodyLine;
    });
  }

  ctx.currentY += bodyLine * 0.15;
}

function renderStandardChapter(ctx: PdfContext, chapter: Chapter, project: Project): void {
  const { pdf, dimensions, baseFontSize, lineHeight, fontName, contentWidth } = ctx;

  // Drop start for chapter openings (start lower on page)
  ctx.currentY = dimensions.marginTop + 35;

  const sectionName = getSectionDisplayName(chapter.sectionType);
  const labelGap = lineMm(baseFontSize * 0.85, lineHeight) + 4;

  // Skip the auto label when the title already starts with the same word
  // (prevents "CHAPTER 1" sitting on top of "Capítulo 1 — …").
  const titleHasLabel =
    /^(cap[íi]tulo|chapter|pr[óo]logo|prologue|ep[íi]logo|epilogue|interl[úu]dio|interlude|parte|part)\b/i.test(
      chapter.title.trim()
    );

  // Section label (small caps style)
  if (sectionName && chapter.sectionType === 'chapter' && !titleHasLabel) {
    pdf.setFontSize(baseFontSize * 0.85);
    pdf.setFont(fontName, 'normal');

    // Count chapter number
    const chapterNum = project.chapters
      .filter(c => c.sectionType === 'chapter')
      .findIndex(c => c.id === chapter.id) + 1;

    const isEnglish = (project.metadata.language || '').toLowerCase().startsWith('en');
    const label = `${isEnglish ? 'CHAPTER' : 'CAPÍTULO'} ${chapterNum}`;
    const labelWidth = pdf.getTextWidth(label);
    pdf.text(label, (dimensions.width - labelWidth) / 2, ctx.currentY);

    ctx.currentY += labelGap;
  } else if (sectionName && chapter.sectionType !== 'chapter' && !titleHasLabel) {
    pdf.setFontSize(baseFontSize * 0.85);
    pdf.setFont(fontName, 'normal');

    const label = sectionName.toUpperCase();
    const labelWidth = pdf.getTextWidth(label);
    pdf.text(label, (dimensions.width - labelWidth) / 2, ctx.currentY);

    ctx.currentY += labelGap;
  }

  // Chapter title
  const titleSize = baseFontSize * 1.6;
  pdf.setFontSize(titleSize);
  pdf.setFont(fontName, 'bold');

  const chapterTitle = chapter.title;
  const titleLines = wrapText(pdf, chapterTitle, contentWidth);

  titleLines.forEach((line) => {
    const lineWidth = pdf.getTextWidth(line);
    pdf.text(line, (dimensions.width - lineWidth) / 2, ctx.currentY);
    ctx.currentY += lineMm(titleSize, 1.15);
  });

  // Space between the chapter title and the first paragraph.
  ctx.currentY += lineMm(baseFontSize, lineHeight) * 2;

  // Chapter content
  pdf.setFontSize(baseFontSize);
  pdf.setFont(fontName, 'normal');

  const bodyLine = lineMm(baseFontSize, lineHeight);
  const contentElements = parseHtmlContent(chapter.content);
  let isFirstParagraph = true;
  let firstBodyParagraph = ctx.dropCaps && DROP_CAP_SECTIONS.includes(chapter.sectionType);

  contentElements.forEach((element) => {
    if (element.type === 'break') {
      ctx.currentY += bodyLine;
      return;
    }

    if (element.type === 'heading') {
      const headingSize =
        baseFontSize * (element.level === 1 ? 1.4 : element.level === 2 ? 1.2 : 1.1);
      checkPageBreak(ctx, lineMm(headingSize, lineHeight) + bodyLine);
      ctx.currentY += bodyLine * 0.6;

      pdf.setFontSize(headingSize);
      pdf.setFont(fontName, 'bold');
      pdf.text(element.text, getLeftMargin(ctx), ctx.currentY);
      ctx.currentY += lineMm(headingSize, lineHeight);

      pdf.setFontSize(baseFontSize);
      pdf.setFont(fontName, 'normal');
      isFirstParagraph = true;
      return;
    }

    if (element.type === 'blockquote') {
      checkPageBreak(ctx, bodyLine * 2);
      pdf.setFont(fontName, 'italic');
      const quoteLines = wrapText(pdf, element.text, contentWidth - 20);

      quoteLines.forEach((line) => {
        checkPageBreak(ctx, bodyLine);
        pdf.text(line, getLeftMargin(ctx) + 10, ctx.currentY);
        ctx.currentY += bodyLine;
      });

      pdf.setFont(fontName, 'normal');
      ctx.currentY += bodyLine * 0.4;
      return;
    }

    // Decorative drop cap on the very first paragraph of a narrative section.
    if (firstBodyParagraph) {
      firstBodyParagraph = false;
      if (/\p{L}/u.test(element.text.charAt(0))) {
        renderDropCapParagraph(ctx, element.text, bodyLine);
        isFirstParagraph = false;
        return;
      }
    }

    const lines = wrapText(pdf, element.text, contentWidth);

    lines.forEach((line, lineIndex) => {
      checkPageBreak(ctx, bodyLine);

      // First-line indent (except the first paragraph after a title/heading).
      const indent = (lineIndex === 0 && !isFirstParagraph) ? lineMm(baseFontSize, 1) * 1.5 : 0;
      pdf.text(line, getLeftMargin(ctx) + indent, ctx.currentY);
      ctx.currentY += bodyLine;
    });

    // Indented paragraphs only need a hair of separation.
    ctx.currentY += bodyLine * 0.15;
    isFirstParagraph = false;
  });
}

/**
 * Walks every physical page once and stamps the folio (bottom, outer corner)
 * and the running head (top), following standard book typography:
 *   • cover / blank pages         → nothing
 *   • front matter                → roman numerals, only on sections flagged in
 *                                   SECTION_SHOW_PAGE_NUMBERS (title/copyright… hidden)
 *   • body + back matter          → arabic, continuous; chapter/part OPENING
 *                                   pages drop the folio (modern convention)
 *   • running head                → author (verso) + chapter/title (recto),
 *                                   never on openings, blanks or front matter
 */
function addPageFurniture(ctx: PdfContext): void {
  const { pdf, dimensions, baseFontSize, fontName, includePageNumbers, headerFooter, project } = ctx;
  const totalPages = pdf.internal.pages.length - 1;
  const headFontSize = baseFontSize * 0.8;

  // Section that controls the current page (carried across continuation pages).
  let curSection: SectionType | undefined;
  let curTitle: string | undefined;

  for (let i = 1; i <= totalPages; i++) {
    const meta = ctx.pageMeta[i] || {
      isRight: i % 2 === 1,
      isBlank: false,
      isCover: false,
      isOpening: false,
    };

    if (meta.isOpening) {
      curSection = meta.sectionType;
      curTitle = meta.chapterTitle;
    }

    if (meta.isCover || meta.isBlank) continue;

    const isFront = i > ctx.coverPages && i < ctx.bodyStartPage;
    const isRight = meta.isRight;
    const outerX = (w: number) =>
      isRight ? dimensions.width - dimensions.marginOuter - w : dimensions.marginOuter;

    pdf.setPage(i);

    // ---- Running head (top) ----
    if (headerFooter && headerFooter !== 'none' && !meta.isOpening && !isFront) {
      let text = '';
      if (isRight) {
        text =
          headerFooter === 'chapter'
            ? curTitle || project.metadata.title || ''
            : headerFooter === 'author'
            ? project.metadata.author || ''
            : project.metadata.title || '';
      } else {
        text = project.metadata.author || project.metadata.title || '';
      }
      if (text) {
        pdf.setFontSize(headFontSize);
        pdf.setFont(fontName, 'normal');
        pdf.text(text, outerX(pdf.getTextWidth(text)), dimensions.marginTop * 0.55);
      }
    }

    // ---- Folio (drop folio: centered at the bottom) ----
    // Standard (Reedsy/CMOS): every text page is numbered. Chapter openings
    // keep their number as a centered "drop folio" (no running head). Only the
    // cover, blanks, title/copyright/dedication/epigraph and Part dividers are
    // left blank — all counting in the sequence.
    if (includePageNumbers) {
      let show: boolean;
      let pageStr: string;
      if (isFront) {
        show = curSection ? SECTION_SHOW_PAGE_NUMBERS[curSection] === true : false;
        pageStr = toRomanNumeral(i - ctx.coverPages);
      } else {
        // body + back matter — continuous arabic; only Part dividers are blank
        show = curSection !== 'part';
        pageStr = (i - ctx.bodyStartPage + 1).toString();
      }
      if (show && pageStr) {
        pdf.setFontSize(headFontSize);
        pdf.setFont(fontName, 'normal');
        const w = pdf.getTextWidth(pageStr);
        pdf.text(pageStr, (dimensions.width - w) / 2, dimensions.height - dimensions.marginBottom / 2);
      }
    }
  }
}

/**
 * Backfills the real page numbers (with dot leaders) into the TOC rows captured
 * during rendering. Runs after pagination, so the body folios are known.
 */
function fillTocPageNumbers(
  ctx: PdfContext,
  pageAssignments: Map<string, { pageNum: number; isRoman: boolean }>
): void {
  const { pdf, dimensions, baseFontSize, fontName } = ctx;
  if (ctx.tocEntries.length === 0) return;

  for (const entry of ctx.tocEntries) {
    const assign = pageAssignments.get(entry.chapterId);
    if (!assign) continue;

    pdf.setPage(entry.page);
    pdf.setFontSize(baseFontSize);
    pdf.setFont(fontName, 'normal');

    const numStr = assign.pageNum.toString();
    const numWidth = pdf.getTextWidth(numStr);
    const meta = ctx.pageMeta[entry.page];
    const isRight = meta ? meta.isRight : entry.page % 2 === 1;
    const rightEdge = dimensions.width - (isRight ? dimensions.marginOuter : dimensions.marginInner);
    const numX = rightEdge - numWidth;

    pdf.text(numStr, numX, entry.y);

    // Dot leaders between the title and the number.
    const dotStart = entry.titleEndX + 1.5;
    const dotEnd = numX - 1.5;
    const dotWidth = pdf.getTextWidth('.');
    if (dotEnd > dotStart && dotWidth > 0) {
      const count = Math.floor((dotEnd - dotStart) / dotWidth);
      if (count > 0) pdf.text('.'.repeat(count), dotStart, entry.y);
    }
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
